import { useState } from "react";
import useToastContext from "hooks/useToastContext";
import { AiFillEdit, AiFillEye, AiFillDelete } from "react-icons/ai";
import { apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { BsArrowUpShort } from "react-icons/bs";

const ManageList = ({ jobsList, allJobs, page, handleEdit, handleView, sort, setSort, handleDeleteJob }) => {
  const notification = useToastContext();
  const { t } = useTranslation();

  // ✅ Keep track of manual overrides for status toggles
  const [statusOverrides, setStatusOverrides] = useState({});

  const handelStatusChange = async (item) => {
    if (item?.adminStatus !== "accepted") {
      notification.error("Job must be accepted by admin before you can change its status");
      return;
    }

    const currentStatus = statusOverrides[item._id] || item?.status;
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    // 🚀 1. Optimistically update the toggle immediately
    setStatusOverrides((prev) => ({ ...prev, [item._id]: newStatus }));

    try {
      const payload = { status: newStatus };
      const path = `${apiPath.jobsStatus}/${item?._id}`;
      const result = await apiPut(path, payload);

      const body = result?.data ?? result;
      const isSuccess =
        result?.success === true ||
        result?.data?.success === true ||
        result?.status === 200 ||
        body?.success === true;

      if (isSuccess) {
        notification.success(body?.message || result?.message || "Status updated");
        // Optional: call allJobs() here if you want to refetch in the background
        // await allJobs();
      } else {
        // ❌ Revert on failure
        setStatusOverrides((prev) => ({ ...prev, [item._id]: currentStatus }));
        notification.error(body?.message || result?.message || "Failed to update status");
      }
    } catch (error) {
      // ❌ Revert on error
      setStatusOverrides((prev) => ({ ...prev, [item._id]: currentStatus }));
      console.error("[status-change] error", error);
      notification.error("Something went wrong");
    }
  };

  return (
    <div>
      <div className="p-3">
        <div className="overflow-x-auto relative rounded-lg border">
          <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400 ">
            <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="py-3 px-6">Sr no.</th>
                <th scope="col" className="py-3 px-6">{t("JOB_TITLE")}</th>
                <th scope="col" className="py-3 px-6">{t("O_COUNTRY")}</th>
                <th scope="col" className="py-3 px-6">{t("O_STATE")}</th>
                <th scope="col" className="py-3 px-6">{t("O_CITY")}</th>
                <th scope="col" className="py-3 px-6">{t("CATEGORY")}</th>
                <th scope="col" className="py-3 px-6">{t("SUBCATEGORY")}</th>
                <th
                  scope="col"
                  className="py-3 px-6 cursor-pointer"
                  onClick={() => {
                    if (sort.sortBy === "createdAt" && sort.sortType === "asc") {
                      setSort({ sortBy: "createdAt", sortType: "desc" });
                    } else {
                      setSort({ sortBy: "createdAt", sortType: "asc" });
                    }
                  }}
                >
                  <div className="flex justify-start">
                    <span>{t("CREATED_DATE")}</span>
                    <span>
                      {sort.sortBy === "createdAt" && sort.sortType === "asc" && <BsArrowUpShort className="w-4 h-4" />}
                      {sort.sortBy === "createdAt" && sort.sortType === "desc" && <BsArrowUpShort className="w-4 h-4 rotate-180" />}
                    </span>
                  </div>
                </th>
                <th scope="col" className="py-3 px-6 text-center">{t("O_ADMINSTATUS")}</th>
                <th scope="col" className="py-3 px-6 text-center">{t("O_STATUS")}</th>
                <th scope="col" className="py-3 px-6 text-center">{t("O_ACTION")}</th>
              </tr>
            </thead>
            <tbody>
              {jobsList &&
                jobsList.map((item, i) => {
                  const effectiveStatus = statusOverrides[item._id] || item?.status;

                  return (
                    <tr key={i} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                      <th scope="row" className="py-4 px-6 border-r font-medium text-gray-900  dark:text-white">
                        {i + 1 + 10 * (page - 1)}
                      </th>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.Country?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.State?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.city}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.Category?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.subCategory?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900 text-left">
                        {dayjs(item?.createdAt).format("MM-DD-YYYY h:mm A")}
                      </td>

                      {/* adminStatus column */}
                      <td className="py-4 px-6 border-r text-gray-900 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            item?.adminStatus === "accepted"
                              ? "bg-green-100 text-green-800"
                              : item?.adminStatus === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {item?.adminStatus
                            ? item.adminStatus.charAt(0).toUpperCase() + item.adminStatus.slice(1)
                            : "Pending"}
                        </span>
                      </td>

                      {/* status column */}
                      <td className="py-4 px-6 border-r text-gray-900 text-center">
                        {item?.adminStatus !== "accepted" ? (
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              item?.adminStatus === "accepted"
                                ? "bg-green-100 text-green-800"
                                : item?.adminStatus === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {item?.adminStatus
                              ? item.adminStatus.charAt(0).toUpperCase() + item.adminStatus.slice(1)
                              : "Pending"}
                          </span>
                        ) : (
                          <label
                            className="inline-flex relative items-center cursor-pointer"
                            title={`${effectiveStatus === "active" ? "Active" : "Inactive"}`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={effectiveStatus === "active"}
                              onChange={() => handelStatusChange(item)} // <-- Direct call, no confirm dialog
                            />
                            <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-focus:ring-0 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-LightBlue" />
                          </label>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6 border-l text-gray-900">
                        <div className="">
                          <ul className="flex justify-center">
                            <li onClick={() => handleEdit(item)} className="px-2 py-2 hover:bg-white hover:text-LightBlue">
                              <a title="Edit" className="hover:text-blue-700 transition duration-150 ease-in-out" data-bs-toggle="tooltip">
                                <AiFillEdit className="cursor-pointer w-5 h-5 text-slate-600" />
                              </a>
                            </li>
                            <li onClick={() => handleView(item)} className="px-2 py-2 hover:bg-white hover:text-LightBlue">
                              <a title="View">
                                <AiFillEye className="cursor-pointer w-5 h-5 text-slate-600" />
                              </a>
                            </li>
                            <li
                              onClick={() => {
                                // using window.confirm just for delete to keep it simple
                                if (window.confirm(`Are you sure want to delete '${item.name}'?`)) {
                                  handleDeleteJob(item._id);
                                }
                              }}
                              className="px-2 py-2 hover:bg-white hover:text-red-500"
                            >
                              <a title="Delete">
                                <AiFillDelete className="cursor-pointer w-5 h-5 text-slate-600" />
                              </a>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {isEmpty(jobsList) ? (
                <tr className="bg-white border-b text-center dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4 px-6 border-r" colSpan={11}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageList;